import BulbOutlined from '@ant-design/icons/BulbOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import CodeOutlined from '@ant-design/icons/CodeOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import ThunderboltOutlined from '@ant-design/icons/ThunderboltOutlined';
import ToolOutlined from '@ant-design/icons/ToolOutlined';
import Alert from 'antd/es/alert';
import Card from 'antd/es/card';
import Empty from 'antd/es/empty';
import Skeleton from 'antd/es/skeleton';
import Timeline from 'antd/es/timeline';
import Typography from 'antd/es/typography';
import React, { useCallback, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import {
  type AgentExecutionStep,
  useAgentExecutionLiveTail,
} from '../../hooks/useAgentExecutionLiveTail';
import MarkdownContent from '../MarkdownContent';

const { Text } = Typography;

const StepContent = styled.div`
  ${({ theme }) => `
  max-width: ${theme.screenXXL / 2}px;
  width: 100%;
`}
`;

const StepHeader = styled.div<{ $hasContent: boolean }>`
  ${({ theme, $hasContent }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.marginXXS}px;
  cursor: ${$hasContent ? 'pointer' : 'default'};
  user-select: none;
  padding: ${theme.paddingXXS}px
    ${theme.paddingXS}px;
  margin: -${theme.paddingXXS}px -${theme.paddingXS}px;
  border-radius: ${theme.borderRadiusSM}px;
  transition: background-color ${theme.motionDurationMid}
    ${theme.motionEaseInOut};

  ${
    $hasContent
      ? `
    &:hover {
      background-color: ${theme.colorBgTextHover};
    }
  `
      : ''
  }
`}
`;

const StepTitleRow = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;
`}
`;

const StepTitle = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;
`}
`;

const StepTimestamp = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
`}
`;

const StepSummary = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
  margin-left: ${theme.marginLG}px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: ${theme.lineHeightHeading4};
`}
`;

const ChevronIcon = styled.span<{ $isExpanded: boolean }>`
  ${({ theme, $isExpanded }) => `
  display: inline-flex;
  transition: transform ${theme.motionDurationMid} ${theme.motionEaseInOut};
  transform: ${$isExpanded ? 'rotate(90deg)' : 'rotate(0)'};
`}
`;

const StepDetails = styled.div<{ $isExpanded: boolean }>`
  ${({ theme, $isExpanded }) => `
  margin-top: ${theme.marginXS}px;
  padding-left: ${theme.marginLG}px;
  max-height: ${$isExpanded ? 'none' : '0'};
  overflow: ${$isExpanded ? 'visible' : 'hidden'};
  opacity: ${$isExpanded ? 1 : 0};
  transition: opacity ${theme.motionDurationMid} ${theme.motionEaseInOut};

  .markdown {
    padding-right: ${theme.paddingXS}px;
  }

  /* Smaller font size for all code blocks */
  pre {
    font-size: ${theme.fontSizeSM}px !important;
  }
`}
`;

const StyledTimeline = styled(Timeline)`
  ${({ theme }) => `
  padding-top: ${theme.paddingLG}px;
`}
`;

interface AgentExecutionLiveTailProps {
  agentExecutionId: string;
  className?: string;
  defaultStepTypes?: string[];
}

function getStepIcon(type: string | undefined) {
  if (!type) return <QuestionCircleOutlined />;

  switch (type) {
    case 'plan':
    case 'planning':
      return <BulbOutlined />;
    case 'tool_call':
      return <ToolOutlined />;
    case 'code':
      return <CodeOutlined />;
    case 'thought':
    case 'reasoning':
      return <RobotOutlined />;
    case 'final_answer':
      return <CheckCircleOutlined />;
    case 'task':
      return <ThunderboltOutlined />;
    case 'observation':
      return <EyeOutlined />;
    case 'fact':
      return <InfoCircleOutlined />;
    case 'system':
      return <SettingOutlined />;
    default:
      return <QuestionCircleOutlined />;
  }
}

// Domain-specific colors for agent execution step types
const STEP_COLORS: Record<string, string> = {
  plan: '#1890ff', // blue
  planning: '#1890ff', // blue
  tool_call: '#52c41a', // green
  code: '#13c2c2', // cyan
  thought: '#fa8c16', // orange
  reasoning: '#fa8c16', // orange
  final_answer: '#722ed1', // purple
  task: '#eb2f96', // magenta
  observation: '#2f54eb', // geekblue
  fact: '#faad14', // gold
  system: '#8c8c8c', // gray
  error: '#f5222d', // red
  default: '#595959', // default gray
};

function getStepColor(type: string | undefined): string {
  return STEP_COLORS[type || 'default'] || STEP_COLORS.default;
}

function formatTimestamp(timestamp?: string | Date): string {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date.toLocaleTimeString();
}

function calculateDuration(
  startedAt?: string | Date,
  endedAt?: string | Date,
): string | null {
  if (!startedAt || !endedAt) return null;

  const start = startedAt instanceof Date ? startedAt : new Date(startedAt);
  const end = endedAt instanceof Date ? endedAt : new Date(endedAt);
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 0) return null;

  // Format duration based on magnitude
  if (durationMs < 1000) {
    return `${durationMs}ms`;
  } else if (durationMs < 60000) {
    const seconds = (durationMs / 1000).toFixed(1);
    return `${seconds}s`;
  } else {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}

// Memoize step content processing to avoid expensive operations
const stepContentCache = new WeakMap<AgentExecutionStep, string | null>();

function getStepContent(step: AgentExecutionStep): string | null {
  // Check cache first using the step object as key
  if (stepContentCache.has(step)) {
    return stepContentCache.get(step)!;
  }

  let content: string | null = null;

  // Extract the actual content based on step type
  switch (step.stepType) {
    case 'task':
      content = step.task || null;
      break;
    case 'thought':
      content = step.thought || null;
      break;
    case 'code':
      content = step.code ? `\`\`\`python\n${step.code}\n\`\`\`` : null;
      break;
    case 'observation':
      // Format observations as log output in a code block
      content = step.observation
        ? `\`\`\`log\n${step.observation}\n\`\`\``
        : null;
      break;
    case 'plan':
      content = step.plan || null;
      break;
    case 'fact':
      content = step.facts || null;
      break;
    case 'system':
      content = step.system || null;
      break;
    case 'final_answer':
      if (step.finalAnswer) {
        content = step.finalAnswer.output;
        if (
          step.finalAnswer.resourceIds &&
          step.finalAnswer.resourceIds.length > 0
        ) {
          content += `\n\n**Resources:** ${step.finalAnswer.resourceIds.join(', ')}`;
        }
      }
      break;
    default:
      // Fallback for any other content
      if (step.content) {
        content = step.content;
      } else if (step.tool) {
        content = `**Tool:** ${step.tool}\n`;
        if (step.input) {
          content += `\n**Input:**\n\`\`\`json\n${JSON.stringify(step.input, null, 2)}\n\`\`\`\n`;
        }
        if (step.output) {
          content += `\n**Output:**\n\`\`\`json\n${JSON.stringify(step.output, null, 2)}\n\`\`\`\n`;
        }
      }
  }

  // Cache the result using WeakMap (automatic garbage collection)
  stepContentCache.set(step, content);

  return content;
}

export const AgentExecutionLiveTail: React.FC<AgentExecutionLiveTailProps> =
  React.memo(({ agentExecutionId, className, defaultStepTypes = [] }) => {
    const { execution, steps, isLoading, error } =
      useAgentExecutionLiveTail(agentExecutionId);
    const [expandedSteps, setExpandedSteps] = useState<number[]>([]);
    const theme = useTheme();

    // No cleanup needed with WeakMap - automatic garbage collection

    const isRunning = execution?.status === 'running';

    // Filter steps based on default types (compact mode) and reverse order (most recent first)
    const filteredSteps = useMemo(() => {
      const filtered =
        defaultStepTypes.length === 0
          ? steps
          : steps.filter((step) => defaultStepTypes.includes(step.stepType));

      // Reverse to show most recent at top
      return [...filtered].reverse();
    }, [steps, defaultStepTypes]);

    // Memoize step expansion toggle to prevent unnecessary re-renders
    const handleStepToggle = useCallback(
      (index: number, hasContent: boolean) => {
        if (!hasContent) return;

        setExpandedSteps((prev) => {
          if (prev.includes(index)) {
            return prev.filter((i) => i !== index);
          } else {
            return [...prev, index];
          }
        });
      },
      [],
    );

    if (isLoading) {
      return (
        <Card className={className}>
          <Skeleton active />
        </Card>
      );
    }

    if (error) {
      return (
        <Card className={className}>
          <Alert
            message="Failed to load execution data"
            description={error.message}
            type="error"
          />
        </Card>
      );
    }

    if (!execution) {
      return (
        <Card className={className}>
          <Empty description="No execution data available" />
        </Card>
      );
    }

    if (filteredSteps.length === 0 && !isRunning) {
      return (
        <Empty
          description={
            defaultStepTypes.length > 0
              ? 'No steps match the selected filters'
              : 'No steps recorded yet'
          }
        />
      );
    }

    return (
      <StyledTimeline className={className}>
        {isRunning && (
          <Timeline.Item
            color={STEP_COLORS.plan}
            dot={<LoadingOutlined spin />}
          >
            <Text type="secondary">Processing next step...</Text>
          </Timeline.Item>
        )}

        {execution.status === 'completed' && (
          <Timeline.Item
            color={STEP_COLORS.tool_call}
            dot={<CheckCircleOutlined />}
          >
            <Text type="success">Execution completed successfully</Text>
          </Timeline.Item>
        )}

        {execution.status === 'failed' && (
          <Timeline.Item
            color={STEP_COLORS.error}
            dot={<CloseCircleOutlined />}
          >
            <Text type="danger">Execution failed</Text>
          </Timeline.Item>
        )}

        {filteredSteps.map((step, index) => {
          const isExpanded = expandedSteps.includes(index);
          const content = getStepContent(step);
          const hasContent = content !== null && content !== '';

          const stepColor = step.error
            ? STEP_COLORS.error
            : getStepColor(step.stepType);

          return (
            <Timeline.Item
              key={index}
              color={stepColor}
              dot={
                step.error ? (
                  <CloseCircleOutlined />
                ) : (
                  getStepIcon(step.stepType)
                )
              }
            >
              <StepContent>
                <StepHeader
                  $hasContent={hasContent}
                  onClick={() => handleStepToggle(index, hasContent)}
                >
                  <StepTitleRow>
                    <StepTitle>
                      {hasContent && (
                        <ChevronIcon $isExpanded={isExpanded}>
                          <RightOutlined />
                        </ChevronIcon>
                      )}
                      <Text>
                        {(step.stepType || 'unknown')
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </Text>
                    </StepTitle>
                    <StepTimestamp type="secondary">
                      {formatTimestamp(step.endedAt || step.timestamp)}
                      {step.startedAt && step.endedAt && (
                        <span style={{ marginLeft: `${theme.marginXS}px` }}>
                          ({calculateDuration(step.startedAt, step.endedAt)})
                        </span>
                      )}
                    </StepTimestamp>
                  </StepTitleRow>
                  {step.summary && (
                    <StepSummary type="secondary">{step.summary}</StepSummary>
                  )}
                </StepHeader>

                {step.error ? (
                  <Alert
                    message="Error"
                    description={step.error}
                    type="error"
                    showIcon
                    style={{ marginTop: `${theme.marginXS}px` }}
                  />
                ) : (
                  hasContent && (
                    <StepDetails $isExpanded={isExpanded}>
                      <MarkdownContent content={content} />
                    </StepDetails>
                  )
                )}
              </StepContent>
            </Timeline.Item>
          );
        })}
      </StyledTimeline>
    );
  });

AgentExecutionLiveTail.displayName = 'AgentExecutionLiveTail';

export default AgentExecutionLiveTail;
